import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookmarkCheck, Plus, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROItemEditDrawer } from "@/components/PROItemEditDrawer";
import { toast } from "sonner";

/**
 * PRO module: create / list / delete split templates.
 * Templates can be unscoped (usable everywhere) or scoped to a group.
 */
export const SplitTemplatesModule = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // form
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [splitType, setSplitType] = useState("equal");
  const [defaultAmount, setDefaultAmount] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [memberValues, setMemberValues] = useState({}); // user_id -> value
  const [schedule, setSchedule] = useState("once");

  const load = async () => {
    try {
      const [t, g, f] = await Promise.all([
        api.get("/split-templates"),
        api.get("/groups"),
        api.get("/friends"),
      ]);
      setItems(t.data);
      setGroups(g.data);
      setFriends(f.data);
    } catch (e) { console.warn("Templates load failed:", e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setName(""); setGroupId(""); setSplitType("equal");
    setDefaultAmount(""); setMemberIds([]); setMemberValues({}); setSchedule("once");
  };

  // Pool of candidate members = self + friends + members of selected group
  const memberPool = (() => {
    const pool = new Map();
    pool.set(user.id, { id: user.id, name: `${user.name} (you)`, avatar_emoji: user.avatar_emoji || "🙂" });
    for (const f of friends) pool.set(f.id, { id: f.id, name: f.name, avatar_emoji: f.avatar_emoji || "🙂" });
    if (groupId) {
      const g = groups.find((x) => x.id === groupId);
      for (const m of (g?.members || [])) pool.set(m.id, { id: m.id, name: m.name, avatar_emoji: m.avatar_emoji || "🙂" });
    }
    return Array.from(pool.values());
  })();

  const toggleMember = (id) => {
    setMemberIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give it a name"); return; }
    if (memberIds.length === 0) { toast.error("Pick at least one person"); return; }
    if (splitType !== "equal") {
      const missing = memberIds.find((id) => !memberValues[id] || Number(memberValues[id]) <= 0);
      if (missing) { toast.error(`Set a value for everyone (${splitType})`); return; }
    }
    setSaving(true);
    try {
      const members = memberIds.map((id) => ({
        user_id: id,
        value: splitType === "equal" ? 1 : Number(memberValues[id]),
      }));
      await api.post("/split-templates", {
        name: name.trim(),
        group_id: groupId || null,
        split_type: splitType,
        members,
        default_amount: defaultAmount ? Number(defaultAmount) : null,
        category: "general",
        schedule,
      });
      toast.success("Template saved");
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    try {
      await api.delete(`/split-templates/${tpl.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card/40 p-6" data-testid="split-templates-module">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md brand-bg text-black flex items-center justify-center">
            <BookmarkCheck className="h-3.5 w-3.5" strokeWidth={2} />
          </div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Pre-Set Splits</div>
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded brand-bg text-black">PRO</span>
        </div>
        <Button onClick={() => setOpen(true)} className="brand-bg text-black hover:opacity-90 h-10" data-testid="new-template-btn">
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New template
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center" data-testid="no-templates">
          <p className="text-sm text-muted-foreground mb-2">Save splits you use again and again — &ldquo;Flat Rent&rdquo;, &ldquo;Office Lunch&rdquo;, &ldquo;Grocery Run&rdquo;.</p>
          <p className="text-xs text-muted-foreground/70">One tap to apply, anywhere in the app.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AnimatePresence initial={false}>
            {items.map((tpl) => (
              <motion.div
                key={tpl.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                onClick={() => setEditItem(tpl)}
                className="rounded-xl border border-border bg-foreground/[0.02] p-4 flex items-start justify-between gap-3 cursor-pointer hover:border-[color:var(--brand)]/40 transition"
                data-testid={`template-card-${tpl.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{tpl.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">
                    {tpl.split_type} · {tpl.members.length} people{tpl.default_amount ? ` · default ${tpl.default_amount}` : ""}
                  </div>
                  {tpl.schedule && tpl.schedule !== "once" && (
                    <div className="text-[10px] font-mono uppercase tracking-wider brand-text mt-0.5">{tpl.schedule}</div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto" data-testid="new-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">New template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-muted border-border h-11" placeholder="Flat Rent" data-testid="tpl-name" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Split type</Label>
                <Select value={splitType} onValueChange={setSplitType}>
                  <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="tpl-split-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="equal">Equal</SelectItem>
                    <SelectItem value="exact">Exact</SelectItem>
                    <SelectItem value="percent">Percent</SelectItem>
                    <SelectItem value="shares">Shares</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Schedule</Label>
                <Select value={schedule} onValueChange={setSchedule}>
                  <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="tpl-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="once">One-time</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Scope to a group (optional)</Label>
              <Select value={groupId || "_none"} onValueChange={(v) => { setGroupId(v === "_none" ? "" : v); setMemberIds([]); setMemberValues({}); }}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="tpl-group"><SelectValue placeholder="Any group" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="_none">Any group / no group</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.emoji} {g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Default amount (optional)</Label>
              <Input type="number" step="0.01" value={defaultAmount} onChange={(e) => setDefaultAmount(e.target.value)} className="mt-1.5 bg-muted border-border h-11 font-mono" placeholder="0.00" data-testid="tpl-amount" />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 block">People</Label>
              <div className="rounded-lg border border-border bg-foreground/[0.02] divide-y divide-border max-h-64 overflow-y-auto">
                {memberPool.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-foreground/5" data-testid={`tpl-member-${m.id}`}>
                    <input type="checkbox" checked={memberIds.includes(m.id)} onChange={() => toggleMember(m.id)} className="h-4 w-4 accent-[var(--brand)]" />
                    <span className="h-8 w-8 rounded-full bg-foreground/5 flex items-center justify-center text-sm shrink-0">{m.avatar_emoji}</span>
                    <span className="text-sm flex-1 truncate">{m.name}</span>
                    {memberIds.includes(m.id) && splitType !== "equal" && (
                      <Input
                        type="number" step="0.01"
                        value={memberValues[m.id] || ""}
                        onChange={(e) => setMemberValues((p) => ({ ...p, [m.id]: e.target.value }))}
                        className="w-24 h-9 bg-muted border-border font-mono text-sm"
                        placeholder={splitType === "percent" ? "%" : (splitType === "shares" ? "shares" : "amount")}
                        data-testid={`tpl-value-${m.id}`}
                      />
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-border h-11 w-full sm:w-auto">Cancel</Button>
            <Button onClick={save} disabled={saving} className="brand-bg text-black hover:opacity-90 h-11 w-full sm:flex-1" data-testid="tpl-save-btn">
              {saving ? "Saving…" : "Save template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PROItemEditDrawer
        open={!!editItem}
        onOpenChange={(o) => !o && setEditItem(null)}
        item={editItem}
        type="template"
        onSaved={() => { setEditItem(null); load(); }}
        onDeleted={() => { setEditItem(null); load(); }}
      />
    </section>
  );
};
